import { Table } from '@tanstack/react-table';
import {
  CaretLeftIcon,
  CaretRightIcon,
  CaretDoubleLeftIcon,
  CaretDoubleRightIcon,
} from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  showSelectedRowCount?: boolean;
  /** Override for page count - use when table.getPageCount() doesn't update reactively */
  pageCount?: number;
  /** Override for page index - use when table.getState().pagination.pageIndex doesn't update reactively */
  pageIndex?: number;
  /** Callback for page changes - use when table methods don't work with manual pagination */
  onPageChange?: (pageIndex: number) => void;
}

export function DataTablePagination<TData>({
  table,
  showSelectedRowCount = false,
  pageCount: pageCountProp,
  pageIndex: pageIndexProp,
  onPageChange,
}: DataTablePaginationProps<TData>) {
  const pageCount = pageCountProp ?? table.getPageCount();
  const pageIndex = pageIndexProp ?? table.getState().pagination.pageIndex;

  // When pageCount is provided as prop, calculate these ourselves
  // since table's internal state may not reflect the correct pageCount
  const canPreviousPage =
    pageCountProp !== undefined ? pageIndex > 0 : table.getCanPreviousPage();
  const canNextPage =
    pageCountProp !== undefined
      ? pageIndex < pageCount - 1
      : table.getCanNextPage();

  const goToPage = (newPageIndex: number) => {
    if (onPageChange) {
      onPageChange(newPageIndex);
    } else {
      table.setPageIndex(newPageIndex);
    }
  };

  return (
    <div className="flex items-center justify-between px-2">
      <div className="text-muted-foreground flex-1 text-sm">
        {showSelectedRowCount && (
          <>
            {table.getFilteredSelectedRowModel().rows.length} of{' '}
            {table.getFilteredRowModel().rows.length} row(s) selected.
          </>
        )}
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Rows per page</p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 25, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          Page {pageIndex + 1} of {pageCount === -1 ? '?' : pageCount}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="icon"
            className="hidden size-8 lg:flex"
            onClick={() => goToPage(0)}
            disabled={!canPreviousPage}
          >
            <span className="sr-only">Go to first page</span>
            <CaretDoubleLeftIcon />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => goToPage(pageIndex - 1)}
            disabled={!canPreviousPage}
          >
            <span className="sr-only">Go to previous page</span>
            <CaretLeftIcon />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => goToPage(pageIndex + 1)}
            disabled={!canNextPage}
          >
            <span className="sr-only">Go to next page</span>
            <CaretRightIcon />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="hidden size-8 lg:flex"
            onClick={() => goToPage(pageCount - 1)}
            disabled={!canNextPage}
          >
            <span className="sr-only">Go to last page</span>
            <CaretDoubleRightIcon />
          </Button>
        </div>
      </div>
    </div>
  );
}
